const runtimeOrigin =
  typeof window !== "undefined" ? window.location.origin : null;
const envBffUrl = process.env.REACT_APP_BFF_URL || null;
let BFF_BASE_URL = envBffUrl || runtimeOrigin || "http://localhost:3001";

if (
  runtimeOrigin &&
  envBffUrl &&
  /localhost|127\\.0\\.0\\.1/.test(envBffUrl) &&
  !/localhost|127\\.0\\.0\\.1/.test(runtimeOrigin)
) {
  BFF_BASE_URL = runtimeOrigin;
}

const authSubscribers = new Set();

async function request(path, options = {}) {
  const url = `${BFF_BASE_URL}${path}`;
  const method = options.method || "POST";
  const headers = { "Content-Type": "application/json" };

  try {
    const response = await fetch(url, {
      method,
      headers,
      credentials: "include",
      body: options.body ? JSON.stringify(options.body) : undefined,
    });

    const json = await response.json().catch(() => null);
    if (!response.ok) {
      return {
        data: null,
        error: { message: json?.error?.message || response.statusText },
      };
    }
    return json;
  } catch (error) {
    return {
      data: null,
      error: { message: error?.message || String(error) },
    };
  }
}

function emitAuth(event, session) {
  authSubscribers.forEach((callback) => {
    callback(event, session);
  });
}

class QueryBuilder {
  constructor(table) {
    this.table = table;
    this.action = "select";
    this.columns = "*";
    this.returning = null;
    this.rows = null;
    this.values = null;
    this.filters = [];
    this.ordering = [];
    this.singleRow = false;
    this.maybeSingleRow = false;
  }

  select(columns = "*") {
    if (this.action === "select") {
      this.columns = columns;
    } else {
      this.returning = columns;
    }
    return this;
  }

  insert(rows) {
    this.action = "insert";
    this.rows = rows;
    return this;
  }

  update(values) {
    this.action = "update";
    this.values = values;
    return this;
  }

  delete() {
    this.action = "delete";
    return this;
  }

  eq(column, value) {
    this.filters.push({ op: "eq", column, value });
    return this;
  }

  ["in"](column, value) {
    this.filters.push({ op: "in", column, value });
    return this;
  }

  order(column, options = {}) {
    this.ordering.push({ column, ascending: options.ascending !== false });
    return this;
  }

  single() {
    this.singleRow = true;
    return this.execute();
  }

  maybeSingle() {
    this.maybeSingleRow = true;
    return this.execute();
  }

  async execute() {
    const payload = {
      table: this.table,
      action: this.action,
      columns: this.columns,
      returning: this.returning,
      rows: this.rows,
      values: this.values,
      filters: this.filters,
      order: this.ordering,
      single: this.singleRow,
      maybeSingle: this.maybeSingleRow,
    };
    return request("/db/query", { body: payload });
  }

  then(resolve, reject) {
    return this.execute().then(resolve, reject);
  }
}

const supabase = {
  auth: {
    async getSession() {
      return request("/auth/session", { method: "GET" });
    },
    async getUser() {
      return request("/auth/user", { method: "GET" });
    },
    signInWithOidc() {
      window.location.href = `${BFF_BASE_URL}/auth/login`;
      return Promise.resolve({ data: null, error: null });
    },
    onAuthStateChange(callback) {
      authSubscribers.add(callback);
      supabase.auth.getSession().then(({ data, error }) => {
        if (error) return;
        const session = data?.session || null;
        emitAuth("INITIAL_SESSION", session);
      });
      return {
        data: {
          subscription: {
            unsubscribe: () => authSubscribers.delete(callback),
          },
        },
      };
    },
    async signInWithOtp() {
      return supabase.auth.signInWithOidc();
    },
    async verifyOtp() {
      return { data: null, error: { message: "OTP not supported" } };
    },
    async signOut() {
      const result = await request("/auth/sign-out", { body: {} });
      if (!result.error) {
        emitAuth("SIGNED_OUT", null);
      }
      return result;
    },
  },
  privacy: {
    async getStatus() {
      return request("/privacy/status", { method: "GET" });
    },
    async acknowledge() {
      return request("/privacy/acknowledge", { body: {} });
    },
  },
  from(table) {
    return new QueryBuilder(table);
  },
  rpc(fn, args) {
    return request("/db/rpc", { body: { fn, args } });
  },
};

export default supabase;
